import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import { useLanguage } from '../../../context/LanguageContext';
import useWorkspaceChange from '../../../hooks/useWorkspaceChange';
import { PageContainer } from '../../../components/Common';
import CampaignEditor from './campaign/CampaignEditor';
import { useGetCommunicationInput, CommunicationInputData } from '../../../hooks/Marketing';

export const NEW_CAMPAIGN_PATH = '/marketing/new-campaign';
export const campaignPath = (id: number | string) => `/marketing/campaigns/${id}`;

// Une campagne = une communication (communication_input côté API) : la page est directement son éditeur.
// /marketing/new-campaign crée une campagne, /marketing/campaigns/:id en reprend une existante.
function NewCampaign() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { fetchCommunicationInput, isReady } = useGetCommunicationInput();

  const [campaign, setCampaign] = useState<CommunicationInputData | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  // Id que l'éditeur vient lui-même de créer : l'URL change mais l'éditeur ne doit pas être rechargé
  const createdHereRef = useRef<number | null>(null);

  const startNewCampaign = useCallback(() => {
    createdHereRef.current = null;
    setCampaign(null);
    setLoadError(false);
    setEditorKey(k => k + 1);
  }, []);

  useEffect(() => {
    if (!id) {
      startNewCampaign();
      return;
    }
    if (Number(id) === createdHereRef.current || !isReady) return;

    let cancelled = false;
    setCampaign(null);
    setLoadError(false);
    fetchCommunicationInput(Number(id)).then(loaded => {
      if (cancelled) return;
      if (!loaded) {
        setLoadError(true);
        return;
      }
      createdHereRef.current = null;
      setCampaign(loaded);
      setEditorKey(k => k + 1);
    });
    return () => { cancelled = true; };
  }, [id, isReady, fetchCommunicationInput, startNewCampaign]);

  const handleCreated = useCallback((communicationInputId: number) => {
    createdHereRef.current = communicationInputId;
    navigate(campaignPath(communicationInputId), { replace: true });
  }, [navigate]);

  const handleDraftSaved = useCallback(() => {
    toast.success('Brouillon enregistré');
  }, []);

  const handleValidated = useCallback(() => {
    toast.success('Campagne validée');
    navigate('/marketing/advanced-search');
  }, [navigate]);

  useWorkspaceChange(() => {
    navigate(NEW_CAMPAIGN_PATH);
    startNewCampaign();
  });

  const loading = !!id && !campaign && !loadError && Number(id) !== createdHereRef.current;

  return (
    // Pleine hauteur, sans marge : l'éditeur de campagne occupe tout l'espace sous l'en-tête
    <PageContainer isDatavizPage sx={{ p: 0, display: 'flex', flexDirection: 'column', overflowY: { xs: 'auto', md: 'hidden' } }}>
      {loadError && <Alert severity="error">Impossible de charger cette campagne.</Alert>}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {!loadError && !loading && (
        <CampaignEditor
          key={editorKey}
          editingComm={campaign}
          onCreated={handleCreated}
          onDraftSaved={handleDraftSaved}
          onValidated={handleValidated}
          headerActions={id && (
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => navigate(NEW_CAMPAIGN_PATH)} sx={{ textTransform: 'none' }}>
              {t('pages.marketing.newAnimation')}
            </Button>
          )}
        />
      )}
    </PageContainer>
  );
}

export default NewCampaign;
